import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { useCreateFamilyAssistance } from "#root/queries/familyAssistance";
import { FILE_KINDS, FILE_REFERENCE_TYPES, useFileUpload } from "#root/hooks/useFileUpload";
import { showToast } from "#root/components/GlobalToaster";
import { ROUTES } from "#root/routes/constants";
import FamilyAssistanceForm, { FamilyAssistanceFormData } from "../FamilyAssistanceForm";
import { LANGUAGE_OPTIONS_LIST, LANGUAGE_OPTIONS } from "#root/utils/settings";
import { Dropdown, Field, Input, Option, Spinner } from "@fluentui/react-components";
import "./styles.scss";
import { CheckmarkFilled } from "@fluentui/react-icons";
import { useIncidentById } from "#root/queries/incidents";
import { withTenantId, useWrappedQuery } from '#root/hooks/useTenantWrappedQuery';
import { useTenantById } from "#root/queries/tenants";

const AddFamilyAssistance: React.FC = () => {
  const navigate = useNavigate();
  const { uploadImages } = useFileUpload();
  const searchParams = new URLSearchParams(window.location.search);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [language, setLanguage] = useState(LANGUAGE_OPTIONS.ENGLISH);

  const defaultFormData: FamilyAssistanceFormData = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    lookingForFirstName: "",
    lookingForLastName: "",
    familyMembersCount: 1,
    counselorPreferences: [0],
    needsChildcare: false,
    hasDisabilities: false,
    specifyDisabilities: [],
    needs: [],
    additionalNeeds: "",
    familyAssistanceImage: null,
  };

  const [formData, setFormData] = useState(defaultFormData);

  const createFamilyAssistance = useCreateFamilyAssistance();

  const tenantId = searchParams.get('tenantId');
  const incidentId = searchParams.get('incidentId');
  
  useEffect(() => {
    if (!tenantId || !incidentId) {
      navigate(ROUTES.ERROR_404);
      return;
    }
  }, [tenantId, incidentId, navigate]);

  const { data: incidentResponse, isLoading: incidentLoading } = useWrappedQuery(useIncidentById, { id: incidentId! }, tenantId!);
  const { data: tenantResponse, isLoading: tenantLoading } = useWrappedQuery(useTenantById, { id: tenantId! }, tenantId!);

    useEffect(() => {
        if (tenantResponse?.item.language) {
            setLanguage(tenantResponse.item.language);
        }
    }, [tenantResponse?.item.language, setLanguage]);

  const onFieldChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (formData: FamilyAssistanceFormData) => {
    try {
      setIsSubmitting(true);

      await withTenantId(async () => {
        const response = await createFamilyAssistance.mutateAsync({
          familyAssistance: {
            ...formData,
            counselorPreferences: formData.counselorPreferences,
            ...(formData.hasDisabilities ? {} : { specifyDisabilities: undefined }),
            incidentId: incidentId!
          }
        });

        if (formData.familyAssistanceImage && response.item?.id) {
          const uploadPayload = [{
            file: formData.familyAssistanceImage,
            referenceId: response.item.id,
            kind: FILE_KINDS.FAMILY_ASSISTANCE,
            referenceType: FILE_REFERENCE_TYPES.FAMILY_ASSISTANCE,
          }];
          await uploadImages(uploadPayload);
        }

        showToast('Record created successfully', 'success');
      }, tenantId!);

      setIsSubmitted(true);
    } catch (error) {
      showToast('Error creating record', 'error');
      console.error(error);
    }
    finally{
      setIsSubmitting(false);
    }
  };

  if (incidentLoading || tenantLoading) {
    return <Spinner size="large" />;
  }

  if (isSubmitted) {
    return (
      <div className="confirmation-container">
        <div className="confirmation">
          <CheckmarkFilled className="success-icon" />
          <div className='subtitle'>Submission completed successfully.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-family-assistance">
      <div className="emc-page-header">
        <div className="emc-page-header__title">
          <h2>Family Assistance Entry <div className="divider"></div></h2>
        </div>
      </div>

      <div className="family-assistance-form-container">
        <Field label="Primary language you speak" required>
          <Dropdown
            value={language}
            selectedOptions={[language]}
            onOptionSelect={(_, data) => {
              const selected = data.selectedOptions[0];
              if (selected) {
                setLanguage(selected);
              }
            }}
          >
            {LANGUAGE_OPTIONS_LIST.map(lang => (
              <Option key={lang} value={lang}>
                {lang}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <p className="instructions">
          Please fill out the following information for each family member so we can best help you and those with you while at the center.
          You will only need to enter this information one time and it will only be shared with officials assisting with reunification and recovery efforts.
        </p>

        <Field label="Incident">
          <Input
            value={incidentResponse?.item.name}
            readOnly
          />
        </Field>

        <FamilyAssistanceForm
          formData={formData}
          onFieldChange={onFieldChange}
          onSubmit={handleSubmit}
          submitButtonText="Submit"
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default AddFamilyAssistance;
